import React, { useEffect, useId, useRef, useState } from "react";
import { ReadableNavButton } from "./Sticker";
import { CheckboxField, FieldHint } from "./createEvent/CreateEventFormUi";
import {
  WorkflowModalBody,
  WorkflowModalFooter,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "./workflow/WorkflowModal";

/**
 * Confirm before leaving a form — Cancel, plus Save or Discard based on a checkbox.
 * Reuse for tab switches, route leaves, and in-form “go elsewhere” links.
 */
export default function ConfirmLeaveModal({
  open,
  onCancel,
  onConfirm,
  busy = false,
  title = "Leave without saving?",
  body = "You have unsaved changes. Save them before continuing, or discard them.",
  checkboxLabel = "Save before you continue",
  cancelLabel = "Cancel",
  saveLabel = "Save",
  discardLabel = "Discard",
  /** Back / leave flows default to discard; opt in when save-first is preferred. */
  defaultSaveChecked = false,
  testId = "leave-confirm-modal",
}) {
  const dialogRef = useRef(null);
  const saveFirstRef = useRef(defaultSaveChecked);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const messageId = `${baseId}-message`;
  const checkboxId = `${baseId}-save`;
  const [saveFirst, setSaveFirst] = useState(defaultSaveChecked);

  useEffect(() => {
    if (!open) return;
    saveFirstRef.current = defaultSaveChecked;
    setSaveFirst(defaultSaveChecked);
  }, [open, defaultSaveChecked]);

  if (!open) return null;

  const confirmLabel = saveFirst ? saveLabel : discardLabel;
  const confirmVariant = saveFirst ? "primary" : "danger";

  function handleConfirm() {
    if (busy) return;
    onConfirm?.({ save: saveFirstRef.current });
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={busy ? undefined : onCancel}
      onBackdropClose={busy ? undefined : onCancel}
      testId={testId}
      titleId={titleId}
      describedById={messageId}
      busy={busy}
      dismissOnEscape={!busy}
      maxWidthClass="sm:max-w-[440px]"
    >
      <WorkflowModalHeader
        title={title}
        titleId={titleId}
        onClose={busy ? undefined : onCancel}
        closeTestId={`${testId}-close`}
      />
      <WorkflowModalBody>
        <FieldHint id={messageId} className="text-[14px]">
          {body}
        </FieldHint>
        <CheckboxField
          id={checkboxId}
          checked={saveFirst}
          onChange={(e) => {
            saveFirstRef.current = e.target.checked;
            setSaveFirst(e.target.checked);
          }}
          title={checkboxLabel}
          testId={`${testId}-save-checkbox`}
        />
      </WorkflowModalBody>
      <WorkflowModalFooter safeArea>
        <div className="flex flex-wrap gap-2 justify-end w-full">
          <ReadableNavButton
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </ReadableNavButton>
          <ReadableNavButton
            type="button"
            variant={confirmVariant}
            disabled={busy}
            onClick={handleConfirm}
            data-testid={`${testId}-confirm`}
            data-save={saveFirst ? "true" : "false"}
          >
            {busy ? "Working…" : confirmLabel}
          </ReadableNavButton>
        </div>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}
